import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';

import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import { resolveRecomputeChartType } from '../../shared/self-service-recompute.utils';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import {
  DropdownOptionOrderMode,
  applyDropdownStyleKeys,
  distinctDropdownOptions,
  normalizeDropdownOptionOrderMode,
  orderDropdownOptions,
} from './dropdown-options.utils';

export interface DropdownOptionGroup {
  label: string;
  displayLabel: string;
  children: string[];
  showAll: boolean;
}

/**
 * Dropdown build form (DROPDOWN): the distinct values of one dataset field
 * become the options; the picked value(s) filter the chosen targets. Renders
 * as a classic select or as always-visible buttons / tiles (with optional
 * submenu groups).
 */
@Component({
  selector: 'app-dropdown-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, NgSelectModule],
  templateUrl: './dropdown-step.component.html',
  styles: [':host { display: contents; }'],
})
export class DropdownStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['DROPDOWN'] as const;
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly ui = inject(SelfServiceUiService);

  dropdownField = '';
  dropdownDisplayLabel = '';
  dropdownIsMultiSelect = false;
  dropdownShowAllOption = false;
  dropdownDefaultValue = '';
  dropdownOptionOrderMode: DropdownOptionOrderMode = 'response';
  dropdownCustomOptionOrder = '';
  dropdownAdvancedAccordionOpen = false;
  dropdownDisplayMode = 'dropdown';
  dropdownGroups: DropdownOptionGroup[] = [];
  /** ng-select `addTag` — lets the user type a value that isn't in the dataset yet. */
  dropdownGroupTag = (term: string) => term.trim();

  private dropdownDisplayModesCache: LabelValueOption[] = [];
  private dropdownDisplayModesKey: unknown = undefined;
  get dropdownDisplayModes(): LabelValueOption[] {
    const fromConfig = (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownDisplayModes'] as LabelValueOption[] | undefined;
    const key = Array.isArray(fromConfig) && fromConfig.length ? fromConfig : 'fallback';
    if (key !== this.dropdownDisplayModesKey) {
      this.dropdownDisplayModesCache = key === 'fallback'
        ? [
            { label: 'Dropdown', value: 'dropdown' },
            { label: 'Buttons', value: 'buttons' },
            { label: 'Tiles', value: 'tiles' },
          ]
        : (fromConfig as LabelValueOption[]);
      this.dropdownDisplayModesKey = key;
    }
    return this.dropdownDisplayModesCache;
  }

  get dropdownGroupShowAllDefault(): boolean {
    return (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownGroupShowAllDefault'] !== false;
  }

  addDropdownGroup(): void {
    this.dropdownGroups = [...this.dropdownGroups, { label: '', displayLabel: '', children: [], showAll: this.dropdownGroupShowAllDefault }];
  }

  removeDropdownGroup(index: number): void {
    this.dropdownGroups = this.dropdownGroups.filter((_, i) => i !== index);
  }

  private dropdownFieldOptionsCache: string[] = [];
  private dropdownFieldOptionsKey = '';
  /** Distinct values of the option field in the cached dataset rows — the pick list for submenu groups. */
  get dropdownFieldOptions(): string[] {
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const key = `${this.host.drillDataset}|${this.dropdownField}|${rows.length}`;
    if (key !== this.dropdownFieldOptionsKey) {
      const seen = new Set<string>();
      if (this.dropdownField) {
        rows.forEach((row) => {
          const value = row?.[this.dropdownField];
          if (value !== null && value !== undefined && String(value).trim() !== '') seen.add(String(value));
        });
      }
      this.dropdownFieldOptionsCache = [...seen];
      this.dropdownFieldOptionsKey = key;
    }
    return this.dropdownFieldOptionsCache;
  }

  private sanitizedDropdownGroups(): DropdownOptionGroup[] {
    const checkboxExposed = this.host.fieldEnabled('dropdown', 'groupShowAll');
    return this.dropdownGroups
      .map((group) => ({
        label: String(group?.label ?? '').trim(),
        displayLabel: String(group?.displayLabel ?? '').trim(),
        children: Array.isArray(group?.children)
          ? group.children.map((child) => String(child).trim()).filter(Boolean)
          : [],
        showAll: checkboxExposed ? group?.showAll !== false : this.dropdownGroupShowAllDefault,
      }))
      .filter((group) => group.label && group.children.length);
  }

  get dropdownOptionOrderModes(): LabelValueOption[] {
    const fromConfig = (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownOptionOrderModes'] as LabelValueOption[] | undefined;
    return Array.isArray(fromConfig) && fromConfig.length
      ? fromConfig
      : [
          { label: 'Backend response order', value: 'response' },
          { label: 'Ascending', value: 'asc' },
          { label: 'Descending', value: 'desc' },
          { label: 'Custom', value: 'custom' },
        ];
  }

  onDropdownFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.dropdownField = mapping.field;
    if (!this.dropdownDisplayLabel) this.dropdownDisplayLabel = mapping.field;
    this.host.invalidatePreview();
  }

  /** The DROPDOWN-scoped values of the shared Format accordion (pill colours / radius / font). */
  dropdownFormatFromAccordion(): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    this.host.applicableFormatControls('DROPDOWN').forEach((control) => {
      const value = this.host.barFormat[control.key];
      if (value !== undefined) format[control.key] = value;
    });
    return format;
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.dropdownField) missing.push('Field');
    if (!this.host.selectedTargetCharts.length) missing.push('At least one target chart');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const defaultValue = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter(Boolean)
      : [];
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'INPUT',
      inputType: 'DROPDOWN',
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      field: this.dropdownField,
      displayLabel: this.dropdownDisplayLabel.trim() || this.host.title.trim(),
      isMultiSelect: this.dropdownIsMultiSelect,
      showAllOption: this.dropdownShowAllOption,
      displayMode: this.dropdownDisplayMode || 'dropdown',
      optionGroups: this.sanitizedDropdownGroups(),
      optionOrderMode: this.dropdownOptionOrderMode,
      customOptionOrder: this.dropdownCustomOptionOrder.trim(),
      capabilities: this.dropdownFormatFromAccordion(),
      defaultValue: this.dropdownIsMultiSelect ? defaultValue : (defaultValue[0] ?? ''),
      value: this.dropdownIsMultiSelect ? defaultValue : (defaultValue.length ? [defaultValue[0]] : []),
      targets: this.host.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.host.targetLabelForId(componentId),
        field: this.dropdownField,
      })),
      ...this.host.apiCallConfigPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    this.dropdownField = String(payload['field'] ?? '');
    this.dropdownDisplayLabel = String(payload['displayLabel'] ?? '');
    this.dropdownIsMultiSelect = !!payload['isMultiSelect'];
    this.dropdownShowAllOption = payload['showAllOption'] === true;
    this.dropdownDisplayMode = String(payload['displayMode'] ?? 'dropdown');
    const savedGroups = Array.isArray(payload['optionGroups'])
      ? payload['optionGroups']
      : tile.dropdownControl?.widgetData?.['optionGroups'];
    this.dropdownGroups = Array.isArray(savedGroups)
      ? (savedGroups as Array<Record<string, unknown>>).map((group) => ({
          label: String(group?.['label'] ?? ''),
          displayLabel: String(group?.['displayLabel'] ?? ''),
          children: Array.isArray(group?.['children']) ? (group['children'] as unknown[]).map(String) : [],
          showAll: group?.['showAll'] !== false,
        }))
      : [];
    this.dropdownOptionOrderMode = normalizeDropdownOptionOrderMode(payload['optionOrderMode'] ?? tile.dropdownControl?.widgetData?.['optionOrderMode']);
    this.dropdownCustomOptionOrder = String(payload['customOptionOrder'] ?? tile.dropdownControl?.widgetData?.['customOptionOrder'] ?? '');
    this.dropdownAdvancedAccordionOpen = this.dropdownOptionOrderMode !== 'response';
    const rawDefault = payload['defaultValue'];
    this.dropdownDefaultValue = Array.isArray(rawDefault) ? rawDefault.map(String).join(', ') : String(rawDefault ?? '');
    const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.host.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, payload['references']);
    const savedTileFormat = {
      ...((payload['capabilities'] as Record<string, unknown>) || {}),
      ...((tile.format || {}) as Record<string, unknown>),
    };
    this.host.applicableFormatControls('DROPDOWN').forEach((control) => {
      if (savedTileFormat[control.key] !== undefined) this.host.barFormat[control.key] = savedTileFormat[control.key];
    });
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.dropdownField = '';
    this.dropdownDisplayLabel = '';
    this.dropdownIsMultiSelect = false;
    this.dropdownShowAllOption = false;
    this.dropdownDefaultValue = '';
    this.dropdownDisplayMode = 'dropdown';
    this.dropdownGroups = [];
    this.dropdownOptionOrderMode = 'response';
    this.dropdownCustomOptionOrder = '';
    this.dropdownAdvancedAccordionOpen = false;
  }

  /** Format accordion changed: fold the DROPDOWN-scoped values into the tile being edited. */
  onFormatChanged(): void {
    if (!this.host.editingTileId) return;
    const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
    const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'DROPDOWN') return;
    tile.format = { ...(tile.format || {}), ...this.dropdownFormatFromAccordion() };
    this.host.onTileFormatChange(tile);
    this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  /** Loads the distinct option values, adds the tile, then pushes its (default) value to the targets. */
  generate(payload: Record<string, unknown>): boolean {
    const filterApiUrl = resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.filterApi, '/getData');
    this.datasetService.getDistinctFieldValues(filterApiUrl, this.host.drillDataset, this.dropdownField).subscribe({
      next: (rows) => {
        const options = distinctDropdownOptions(rows, this.dropdownField);
        this.host.isGenerating = false;
        const dropdownTile = this.buildTile(payload, options);
        const updated = this.host.addTile(dropdownTile);
        this.ui.showSuccess(updated ? 'Dropdown updated on the dashboard.' : 'Dropdown added to the dashboard.');
        const persisted = this.host.dashboardTiles.find((tile) => tile.id === dropdownTile.id) ?? dropdownTile;
        const value = persisted.dropdownControl?.value ?? [];
        this.host.onTileDropdownChange(persisted, value, payload);
      },
      error: (error: HttpErrorResponse) => {
        this.host.isGenerating = false;
        this.host.generateError = error.error?.message || error.message || 'Unable to load dropdown option values.';
        this.ui.showError(this.host.generateError);
      },
    });
    return true;
  }

  private buildTile(payload: Record<string, unknown>, options: string[]): DashboardTile {
    const base = this.host.newTileBase(payload);
    const orderedOptions = orderDropdownOptions(options, this.dropdownOptionOrderMode, this.dropdownCustomOptionOrder);
    const defaultValues = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter((value) => orderedOptions.includes(value))
      : [];
    const value = this.dropdownIsMultiSelect ? defaultValues : defaultValues.slice(0, 1);
    const isButtonMode = this.dropdownDisplayMode === 'buttons' || this.dropdownDisplayMode === 'tiles';
    const dropdownFormat = this.dropdownFormatFromAccordion();
    const widgetData: Record<string, unknown> = {
      name: this.dropdownDisplayLabel.trim() || this.host.title.trim() || this.dropdownField,
      key: this.dropdownField,
      isMultiSelect: this.dropdownIsMultiSelect,
      showAllOption: this.dropdownShowAllOption,
      displayMode: this.dropdownDisplayMode || 'dropdown',
      optionGroups: this.sanitizedDropdownGroups(),
      optionOrderMode: this.dropdownOptionOrderMode,
      customOptionOrder: this.dropdownCustomOptionOrder.trim(),
      textCase: 'capital',
      optionsList: orderedOptions,
    };
    applyDropdownStyleKeys(dropdownFormat, widgetData);
    return {
      ...base,
      payload: { ...payload, value },
      colSpan: 4,
      rowSpan: isButtonMode ? 5 : 3,
      format: dropdownFormat,
      dropdownControl: {
        field: this.dropdownField,
        options: orderedOptions,
        value,
        targets: [...this.host.selectedTargetCharts],
        widgetData,
        apiConfig: { ...this.host.buildApiCallConfig },
      },
    };
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.dropdownControl) return;
    const widgetData: Record<string, unknown> = { ...(tile.dropdownControl.widgetData || {}) };
    applyDropdownStyleKeys(format, widgetData);
    tile.dropdownControl = { ...tile.dropdownControl, widgetData };
    const capabilities: Record<string, unknown> = {};
    applyDropdownStyleKeys(format, capabilities);
    tile.payload = { ...tile.payload, capabilities };
  }

  /** Only an INPUT-typed result feeds a dropdown: its rows become the options (keeping still-valid selections). */
  applyRecomputeResult(tile: DashboardTile, { entry, result }: TileRecomputeResult): boolean {
    if (resolveRecomputeChartType(entry) !== 'INPUT' || !tile.dropdownControl) return false;
    const rawOptions = Array.isArray(result['data']) ? (result['data'] as unknown[]) : [];
    const options = rawOptions
      .map((item) => (item && typeof item === 'object' ? String((item as Record<string, unknown>)['value'] ?? '') : String(item ?? '')))
      .filter(Boolean);
    const value = tile.dropdownControl.value.filter((v) => options.includes(v));
    tile.payload = { ...tile.payload, value };
    tile.dropdownControl = { ...tile.dropdownControl, options, value };
    return true;
  }
}
